import {
  useBiggestDurationTrendChartBar,
  useShouldHideProfitCategories,
  useTrendChartBarsArray,
} from "utils/customHooks/trendChartHooks";
import type {
  RollupWithTagsAndCountsAndRange,
  TimeRangeFinite,
} from "types/types";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useTrendChartBinSize,
  useGetPageFromPath,
  useFocusRange,
  useTargetRateForTrendBar,
} from "utils/customHooks/genericHooks";
import { TREND_CHART_CONFIG } from "constants/customers";
import {
  useLotInfoForSingleProcessId,
  useProcessIdOnSingleProcessPage,
} from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { ONE_HOUR_OF_MS } from "utils/timeUtils";
import { CENTER_LABEL_HEIGHT } from "components/Dashboard/TrendChart/TrendChartStyles";
import { useCurrentTrendChartBarOutput } from "../../trendChartContentUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useMemo } from "react";

/** data used for the trend line + target trend line
 * * only works for _hourly_ trend chart right now (timeline/hublet/grid pages)
 */
// tslint:disable-next-line: cognitive-complexity
export function useTrendLineSegment({
  rollupMetric,
  barRange,
  barHeight,
  prevRollupMetric,
}: {
  rollupMetric: RollupWithTagsAndCountsAndRange;
  barRange: TimeRangeFinite;
  barHeight: number;
  prevRollupMetric: RollupWithTagsAndCountsAndRange | null;
}) {
  const { isTrendsPage } = useGetPageFromPath();
  const { trendChartBinSizeMs } = useTrendChartBinSize();
  const ckey = useCustomerKey();
  const lotInfoForProcess = useLotInfoForSingleProcessId();
  const processId = useProcessIdOnSingleProcessPage();
  const config = TREND_CHART_CONFIG[ckey];
  const biggestDurationTrendChartBarRollup = useBiggestDurationTrendChartBar();
  const currentCumulativeOutput = useCurrentTrendChartBarOutput();
  const shouldHideProfitCategories = useShouldHideProfitCategories();
  const [
    {
      general: { enableLotInfoModule: shouldUseLotProgress },
      trendChart,
    },
  ] = useFlightConfig();

  const focusRange = useFocusRange();

  const rollupMetricTrendChartBarsArray = useTrendChartBarsArray(isTrendsPage);

  const targetRate = useTargetRateForTrendBar(processId, barRange.ge);

  const showTrendLine = trendChart.showTrendLine;
  const showTargetTrendLine = trendChart.showTargetTrendLine;

  return useMemo(() => {
    // calculate axis height from bar height; depends if we're showing/hiding profit categories
    const axisHeight = shouldHideProfitCategories
      ? barHeight
      : 2 * barHeight + CENTER_LABEL_HEIGHT;

    const axisScaleY = biggestDurationTrendChartBarRollup?.duration
      ? biggestDurationTrendChartBarRollup?.duration / trendChartBinSizeMs
      : 1;
    const maxOutput = trendChart.maxOutput ?? config?.maxOutput;
    const maxOutputPossible = shouldUseLotProgress // if using lot progress, max output = lot size
      ? lotInfoForProcess?.lotSize && lotInfoForProcess.lotSize * 1.1 // (add 10% margin in case they go over)
      : maxOutput // else it's configured
        ? Number(maxOutput) * axisScaleY
        : null;

    // ball height = percent output achieved * barHeight

    // if they've gone over the maximum, use the current value instead, so the trend line doesn't go over the top
    const maxOutputOrAbove = Math.max(
      maxOutputPossible || 0,
      currentCumulativeOutput || 0
    );

    const percentOutputAchieved = maxOutputPossible
      ? (rollupMetric?.cumulativeOutput || 0) / maxOutputOrAbove
      : 0;

    const ballHeightPx = axisHeight * percentOutputAchieved;

    const prevPercentOutputAchieved =
      maxOutputPossible && prevRollupMetric
        ? (prevRollupMetric?.cumulativeOutput || 0) / maxOutputOrAbove
        : 0;

    const prevBallHeightPx =
      prevPercentOutputAchieved || prevPercentOutputAchieved === 0
        ? axisHeight * prevPercentOutputAchieved
        : 0;

    // dashed target line height = percent output we should have achieved * barHeight

    const maxOutputForShift = maxOutput;

    // "production time" = all categories except NPS time

    // how much "non-production" (NPS) time so far?
    const rollupsUpToThisBar = rollupMetricTrendChartBarsArray.filter(
      ({ range }) => range.ge <= barRange.ge
    );
    const NPSTimeSoFar = rollupsUpToThisBar.reduce(
      (acc, cur) => acc + cur.notScheduled,
      0
    );
    // prev hour?
    const rollupsUpToPrevBar = rollupsUpToThisBar.slice(0, -1);
    const prevNPSTimeSoFar = rollupsUpToPrevBar.reduce(
      (acc, cur) => acc + cur.notScheduled,
      0
    );
    const plannedTimeSoFar = rollupsUpToThisBar.reduce(
      (acc, cur) => acc + cur.planned,
      0
    );

    const isPciuk = ckey === "pciuk";

    // how much producing time has passed at this bar, since start of shift?
    const prodTimeSoFar = focusRange
      ? barRange.lt -
        focusRange.ge -
        NPSTimeSoFar -
        (isPciuk ? plannedTimeSoFar : 0)
      : 0;
    // prev bar?
    const prevProdTimeSoFar = focusRange
      ? barRange.ge -
        focusRange.ge -
        prevNPSTimeSoFar -
        (isPciuk ? plannedTimeSoFar : 0)
      : 0;

    // target so far = prodTimeSoFar/hr * target rate/hr
    const targetSoFar =
      prodTimeSoFar && targetRate
        ? (prodTimeSoFar / ONE_HOUR_OF_MS) * targetRate
        : 0;
    // prev target so far = (prevProdTimeSoFar)/hr * target rate/hr
    const prevTargetSoFar =
      prevProdTimeSoFar && targetRate
        ? (prevProdTimeSoFar / ONE_HOUR_OF_MS) * targetRate
        : 0;

    // target line height = target so far / target for shift * bar height
    const targetLineHeight =
      targetSoFar && maxOutputForShift
        ? (targetSoFar / maxOutputForShift) * axisHeight
        : 0;

    const prevTargetLineHeight =
      prevTargetSoFar && maxOutputForShift
        ? (prevTargetSoFar / maxOutputForShift) * axisHeight
        : 0;

    const shouldShowTrendLine =
      !isTrendsPage && showTrendLine && percentOutputAchieved !== 0;
    const shouldShowTargetTrendLine = !isTrendsPage && showTargetTrendLine;

    // trend lines move up by CENTER_LABEL_HEIGHT if we're hiding profit categories
    const dy = shouldHideProfitCategories ? CENTER_LABEL_HEIGHT : 0;

    return {
      shouldShowTargetTrendLine,
      shouldShowTrendLine,
      ballHeightPx: ballHeightPx + dy,
      prevBallHeightPx: prevBallHeightPx + dy,
      targetLineHeight: targetLineHeight + dy,
      prevTargetLineHeight: prevTargetLineHeight + dy,
    };
  }, [
    barHeight,
    biggestDurationTrendChartBarRollup?.duration,
    ckey,
    config?.maxOutput,
    currentCumulativeOutput,
    trendChart.maxOutput,
    showTargetTrendLine,
    focusRange,
    isTrendsPage,
    lotInfoForProcess.lotSize,
    prevRollupMetric,
    rollupMetric?.cumulativeOutput,
    barRange.ge,
    barRange.lt,
    rollupMetricTrendChartBarsArray,
    shouldHideProfitCategories,
    shouldUseLotProgress,
    showTrendLine,
    targetRate,
    trendChartBinSizeMs,
  ]);
}

/** only show trend line on pages with one process */
export function useShowTrendLineOnThisPage() {
  const { isHubletPage, isGridLayoutPage } = useGetPageFromPath();
  return isHubletPage || isGridLayoutPage;
}
